import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { loginPathWithNext } from '../../config/loginRedirect';
import { useAuth } from '../../contexts/useAuth';

interface ProtectedRouteProps {
  redirectPath?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  redirectPath = '/login'
}) => {
  const { isAuthenticated, isLoading, authUnavailable, retryAuth } = useAuth();
  const location = useLocation();

  // `isLoading` is the only thing worth waiting on — it's true exactly while the auth
  // check is in flight. This also used to gate on a fixed 5s "startup grace period"
  // intended to let auth retries finish, but checkAuthStatus is a single attempt, so
  // nothing could change during the wait. It only delayed every protected page load
  // (measured: ~5.4s of spinner after auth had already succeeded).
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div
          data-testid="loading-spinner"
          className="w-12 h-12 border-4 border-gray-300 rounded-full border-t-brand-500 animate-spin"
        ></div>
      </div>
    );
  }

  // The server didn't answer the session check — the user may well still be signed in, so don't
  // send them to /login over a blip.
  if (!isAuthenticated && authUnavailable) {
    return (
      <AuthUnavailable
        onRetry={() => void retryAuth()}
        signInPath={loginPathWithNext(location.pathname, location.search)}
      />
    );
  }

  if (!isAuthenticated) {
    // Signing in brings them back to the deep link they opened (SignInForm honours `next`).
    const to = redirectPath === '/login' ? loginPathWithNext(location.pathname, location.search) : redirectPath;
    return <Navigate to={to} replace />;
  }

  return <Outlet />;
};

function AuthUnavailable({ onRetry, signInPath }: { onRetry: () => void; signInPath: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6 dark:bg-gray-900">
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm dark:border-gray-800 dark:bg-gray-800">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Can't reach DA Connect right now</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Check your connection and try again. You haven't been signed out.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
        >
          Retry
        </button>
        <p className="mt-4 text-sm">
          <Link to={signInPath} className="font-medium text-brand-500 hover:text-brand-600 dark:text-brand-400">
            Sign in again
          </Link>
        </p>
      </div>
    </div>
  );
}
